import type * as IWidget from "app/components/widget/widget-types/Widget";
import type * as TrafficSources from "./dashboardWizardTrafficSourceGenerator";
import type { IWidgetModelTypesType } from "../../../widget/widget-types/Widget";
import type { ICampaignsDataUnion } from "app/components/campaigns/shared/CampaignsConst";
import type { ISelectedSites } from "app/pages/digital-marketing/keywords-campaigns/reports/keywords-report/keywords-table/KeywordsReportTypes";

export interface IDashboardWizardFormElements {
    autocomplete: IDashboardWizardFormElement;
    webSource: IDashboardWizardFormElement;
    duration: IDashboardWizardFormElement;
    country: IDashboardWizardFormElement;
    type: IDashboardWizardFormElement;
    trafficChannels: IDashboardWizardFormElement;
    device: IDashboardWizardFormElement;
}

interface IDashboardWizardFormElement {
    isActive: boolean;
    isVisible: boolean;
}

export type AvaliableDurations = {
    buttonText: string;
    displayText: string;
    value: string;
    enabled: boolean;
};

interface IDashboardWidgetWizardState {
    widget: IWidget.IWidgetModel;
    isCompare: boolean;
    isEditing: boolean;
    allCampaignsConfigurations?: ICampaignsDataUnion;
    component: string;
    mobileWebComponent: string;
    totalComponent: string;
    formElements: IDashboardWizardFormElements;
    availableTrafficSources: Array<TrafficSources.TrafficSource>;
    disabledDurations: string[];
    availableDurations: AvaliableDurations[];
    minDate?: any;
    maxDate?: any;
    availableCountries: any;
    availableWidgetTypes: any;
    availableFilters: any;
    comparedDurationItems?: any;
    appStore?: any;
    selectedWidgetType?: IWidgetModelTypesType;
    disableDatepicker?: boolean;
    disableCountryFilter?: boolean;
    isCountryFilterHidden?: boolean;
    androidOnly?: boolean;
    iosOnly?: boolean;
    selectedSites?: ISelectedSites[];
}

export default function getDefaultState(): IDashboardWidgetWizardState {
    return {
        widget: {
            key: [],
            id: "",
            selectedChannel: "Direct",
            comparedDuration: undefined,
            metric: "EngagementVisits",
            webSource: "Total",
            duration: "3m",
            country: 999,
            type: "Graph",
            filters: {
                // Default to Monthly so metrics that infer Daily from duration (e.g. 28d / window) still work,
                // and Market Shares graph keeps legacy non-window → Monthly when no explicit granularity is chosen.
                timeGranularity: "Monthly",
            },
            width: 2,
            customAsset: false,
            currentCampaignConfiguration: null,
        },
        isEditing: false,
        allCampaignsConfigurations: null,
        selectedWidgetType: "Graph",
        isCompare: true,
        component: "WebAnalysis",
        mobileWebComponent: "",
        totalComponent: "",
        selectedSites: [],
        formElements: {
            autocomplete: {
                isActive: true,
                isVisible: true,
            },
            webSource: {
                isActive: true,
                isVisible: true,
            },
            duration: {
                isActive: true,
                isVisible: true,
            },
            country: {
                isActive: true,
                isVisible: true,
            },
            type: {
                isActive: true,
                isVisible: true,
            },
            trafficChannels: {
                isActive: false,
                isVisible: false,
            },
            device: {
                isActive: false,
                isVisible: false,
            },
        },
        availableTrafficSources: [
            {
                id: "Total",
                text: "analysis.single.trafficsources.tabs.total",
                icon: "sw-icon-widget-total",
                upgrade: false,
            },
            {
                id: "Desktop",
                text: "analysis.audience.mobileweb.desktop.traffic",
                icon: "desktop",
                upgrade: false,
            },
            {
                id: "MobileWeb",
                text: "analysis.audience.overview.mobile",
                icon: "mobile-web",
                upgrade: false,
            },
        ],
        availableDurations: [
            {
                buttonText: "Last 28 Days",
                displayText: "Last 28 Days (As of Jun 08)",
                value: "28d",
                enabled: true,
            },
            {
                buttonText: "Last 1 month",
                displayText: "May 17 - May 17 (1 month)",
                value: "1m",
                enabled: true,
            },
            {
                buttonText: "Last 3 months",
                displayText: "Mar 17 - May 17 (3 months)",
                value: "3m",
                enabled: true,
            },
            {
                buttonText: "Last 6 months",
                displayText: "Dec 16 - May 17 (6 months)",
                value: "6m",
                enabled: false,
            },
            {
                buttonText: "Last 12 months",
                displayText: "Jun 16 - May 17 (12 months)",
                value: "12m",
                enabled: false,
            },
            {
                buttonText: "Last 25 months",
                displayText: "May 15 - May 17 (25 months)",
                value: "25m",
                enabled: false,
            },
            {
                buttonText: "Last 37 months",
                displayText: "Apr 14 - May 17 (37 months)",
                value: "37m",
                enabled: false,
            },
        ],
        disabledDurations: [],
        availableCountries: [
            {
                id: 999,
                code: "ww",
                children: [],
                icon: "flag flag-ww",
                text: "WorldWide",
            },
        ],
        availableWidgetTypes: [
            {
                title: "dashboard.widgets.types.table",
                order: "3",
                width: 2,
                id: "Table",
                dashboard: true,
                type: "Table",
                disabled: true,
            },
            {
                title: "dashboard.widgets.types.graph",
                order: "2",
                width: 2,
                id: "Graph",
                dashboard: true,
                type: "Graph",
                disabled: false,
            },
            {
                title: "dashboard.widgets.types.pie",
                order: "1",
                width: 1,
                id: "PieChart",
                dashboard: true,
                type: "PieChart",
                disabled: false,
            },
            {
                title: "dashboard.widgets.types.bar",
                order: "5",
                width: 2,
                id: "BarChart",
                dashboard: true,
                type: "BarChart",
                disabled: true,
            },
            {
                title: "dashboard.widgets.types.single",
                order: "4",
                width: 1,
                id: "SingleMetric",
                dashboard: true,
                type: "SingleMetric",
                disabled: false,
            },
        ],
        availableFilters: [],
        comparedDurationItems: [],
        disableDatepicker: false,
        disableCountryFilter: false,
        isCountryFilterHidden: false,
    };
}
